import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaCopy, FaEject, FaGamepad, FaInfoCircle, FaTrashAlt, FaUndo, FaUserAltSlash } from 'react-icons/fa';
import { FaWandMagicSparkles, FaEnvelope, FaComputer } from 'react-icons/fa6';
import * as Motion from 'motion/react';
import DeviceImage from '../../components/DeviceImage';
import DynamicGameUI from '../../components/DynamicGameUI';
import Loader from '../../components/Loaders';
import useAuth from '../../hooks/useAuth';
import useDialogBox from '../../hooks/useDialogBox';
import useSocket from '../../hooks/useSocket';
import { deleteDevice, getDevice, requestRegistrationCode, unregisterDevice } from '../../api/v1/devices';
import { formatDate, getDevicePrettyName } from '../../util';
import { animationStaggeredItem } from '../../animations';
import '../../styles/screen.scss';
import ConnectionState from '../../components/ConnectionState';

const { motion } = Motion;


const Device = () => {
  const formRef = useRef(null);
  const dynamicUIRef = useRef(null);
  const [device, setDevice] = useState([]);
  const [activeSpecialistId, setActiveSpecialistId] = useState(null);
  const [activeSpecialistUserName, setActiveSpecialistUserName] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [consoleOutput, setConsoleOutput] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { resetDialogBox, setDialogBox } = useDialogBox();
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const { isConnected, send, subscribe } = useSocket();

  const [connectedIds, setConnectedIds] = useState(new Set());
  const [isDeviceConnected, setIsDeviceConnected] = useState(false);

  const { user } = useAuth();

  const loadDevice = async () => {
    const result = await getDevice(deviceId);

    setDevice(result?.data);
  };

  const confirmUnregisterDevice = async (deviceId) => {
    setDialogBox({
      title: 'Unregister Device',
      content: <p>Do you want to unregister <b>{deviceId}</b> from it's user?</p>,
      actions: [
        { title: 'Unregister Device', type: 'danger', action: async () => await unregisterDeviceSubmit(deviceId) },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  const forceDeviceDisconnect = async () => {
    send(
      JSON.stringify({
        channel: 'forceDeviceDisconnect',
        payload: {
          deviceId: device.deviceId,
        }
      })
    );
  };

  const toastDialog = async () => {
    setDialogBox({
      title: 'Send Message To Device',
      content: <ToastMessageForm ref={formRef}/>,
      actions: [
        { title: 'Send Message', type: 'primary', action: async () => await sendToastMessage() },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  const unregisterDeviceSubmit = async (id) => {
    await unregisterDevice(id);
    await loadDevice();
    resetDialogBox();
  };

  const confirmDeleteDevice = async (deviceId) => {
    setDialogBox({
      title: 'Delete Device',
      content: <p>Do you want to delete device <b>{deviceId}</b>?</p>,
      actions: [
        { title: 'Delete Device', type: 'danger', action: async () => await handleDeleteDevice(deviceId) },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  const handleDeleteDevice = async (deviceId) => {
    await deleteDevice(deviceId);
    resetDialogBox();

    navigate(-1);
  };

  const handleRequestRegistrationCode = async (id) => {
    await requestRegistrationCode(id)

    await loadDevice();
  };

  const isTakeControlButtonDisabled = () => {
    return activeSpecialistId && activeSpecialistId !== user.id;
  }

  const sendToastMessage = async () => {
    const { toastMessage } = formRef.current?.getValues();

    send(
      JSON.stringify({
        channel: 'sendToastMessage',
        payload: {
          message: toastMessage,
        }
      })
    );
  }

  const sendSpecialistConnectRequest = () => {
    send(
      JSON.stringify({
        channel: 'specialistConnect',
        payload: {
          deviceId: deviceId,
          specialistId: user.id,
          specialistUserName: user.userName
        }
      })
    );

    send(JSON.stringify({
      channel: 'command',
      metadata: {
        version: 'v2.0',
        ID: 0
      },
      payload: {
        deviceId: deviceId,
        command: 262154
        // command: 262157
      }
    }));

    forceDetailsUpdate(deviceId);
  }

  const sendPayloadToDevice = (message) => {
    send(
      JSON.stringify({
        channel: 'sendPayloadToDevice',
        payload: {
          deviceId: deviceId,
          message: message,
        }
      })
    );

    console.log(`Sent payload to device ${deviceId}`);
  }

  const forceDetailsUpdate = (deviceId) => {
    send(
      JSON.stringify({
        channel: 'checkForWaitingSpecialist',
        payload: {
          deviceId: deviceId,
        }
      })
    );
  }

  const sendSpecialistDisconnectRequest = () => {
    send(
      JSON.stringify({
        channel: 'specialistDisconnect',
        payload: {
          deviceId: deviceId,
          specialistId: user.id,
          specialistUserName: user.userName
        }
      })
    );
  }

  useEffect(() => {
    (async () => {
      await loadDevice();
      setIsLoading(false);

      if (isConnected) {
        window.sendPayloadToDevice = sendPayloadToDevice;
      }
    })();
  }, [deviceId, isConnected]);

  useEffect(() => {
    if (isConnected && deviceId) {
      const unsubscribe = subscribe(`specialistDetailsResponse`, (message) => {
        const { deviceId: clientDeviceId, specialistId, specialistUserName, connected } = message?.payload
        if (clientDeviceId === deviceId) {
          setActiveSpecialistId(connected ? specialistId : null);
          setActiveSpecialistUserName(connected ? specialistUserName : null);
        }
      });

      send(JSON.stringify({
        channel: 'checkForWaitingSpecialist', payload: {
          deviceId: deviceId,
        }
      }));

      return unsubscribe;
    }
  }, [isConnected, deviceId]);

  useEffect(() => {
    if (isConnected && deviceId) {
      const unsubscribe = subscribe(`gameState-device-${deviceId}-response`, (message) => {
        setGameState(message?.payload?.gameState);
      });

      return unsubscribe;
    }
  }, [isConnected, deviceId]);

  useEffect(() => {
    if (isConnected && deviceId) {
      const unsubscribe = subscribe(`device-${deviceId}-console-message`, (message) => {
        setConsoleOutput(message?.payload?.content);
      });

      return unsubscribe;
    }
  }, [isConnected, deviceId]);

  useEffect(() => {
    if (deviceId && isConnected) {
      const unsubscribe = subscribe(`devicesResponse`, (message) => {
        setConnectedIds(new Set(message.payload.map(d => d.deviceId)));
      });

      send(JSON.stringify({ channel: 'devicesRequest' }));

      return unsubscribe;
    }
  }, [deviceId, isConnected]);

  useEffect(() => {
    if (deviceId && isConnected) {
      return () => {
        send(
          JSON.stringify({
            channel: 'specialistDisconnect',
            payload: {
              deviceId: deviceId,
              specialistId: user.id,
            }
          })
        );
      }
    }
  }, [deviceId, isConnected]);

  useEffect(() => {
    if (deviceId) {
      setIsDeviceConnected(connectedIds.has(deviceId));
    }
  }, [deviceId, connectedIds]);

  useEffect(() => {
    if (!isDeviceConnected) {
      setActiveSpecialistId(null);
      setActiveSpecialistUserName(null);
    }
  }, [isDeviceConnected]);

  return (
    <>
      <Motion.AnimatePresence>
        <Motion.AnimatePresence>
          {isLoading && <Loader/>}
        </Motion.AnimatePresence>
      </Motion.AnimatePresence>

      <div className="screen-navigation">
        <button className="screen-back-button" onClick={() => navigate(-1)}>&larr; Go Back</button>
      </div>

      <motion.div variants={animationStaggeredItem} className="screen-header">
        <div className="screen-title-wrapper">
          <DeviceImage deviceType={device?.deviceType}/>

          <div>
            <h2 className="screen-title">
              {device?.User?.userName ? `${device?.User?.userName}'s ` : ''}
              {getDevicePrettyName(device?.deviceType)}
              <ConnectionState isConnected={isDeviceConnected}/>
              {
                isDeviceConnected &&
                <button className="screen-header-button secondary" onClick={() => forceDeviceDisconnect(deviceId)}>
                  <FaEject />
                </button>
              }
            </h2>

            <p className="screen-subtitle">
              <b>Device ID:</b> {device?.deviceId}
            </p>
          </div>
        </div>

        <div className={`screen-header-button-row`}>
          {
            device?.isRegistered &&
            <button className="screen-header-button secondary" onClick={() => confirmUnregisterDevice(deviceId)}>
              <FaUserAltSlash/>
              Unregister Device
            </button>
          }

          <button className="screen-header-button danger" onClick={() => confirmDeleteDevice(deviceId)}>
            <FaTrashAlt/>
            Delete Device
          </button>
        </div>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-section section-row">
        <div>
          <div className="section-heading-wrapper">
            <div className="section-heading-group-row no-justify">
              <h3 className="section-heading">Device Details</h3>
            </div>
          </div>

          <div className="section-row">
            <div className="section-row-column">
              <p>
                <b>Conductor ID:</b>
                &nbsp;
                {device?.id}
              </p>

              <p>
                <b>Device ID:</b>
                &nbsp;
                {device?.deviceId}
              </p>

              <p>
                <b>Device Type:</b>
                &nbsp;
                {getDevicePrettyName(device?.deviceType)}
              </p>

              <p>
                <b>Last Connected:</b>
                &nbsp;
                {
                  device?.lastConnected &&
                  formatDate(device?.lastConnected, null, true)
                  || <i>never</i>
                }
              </p>
            </div>

            <div className="section-row-column">
              <p>
                <b>Registration Status:</b>
                &nbsp;
                {device?.isRegistered ? 'Registered' : 'Not Registered'}
              </p>

              <p>
                <b>Registered User:</b>
                &nbsp;
                {
                  device?.User?.userName ?
                    <Link to={`/users/${device?.User?.id}`}>{device?.User?.userName}</Link> :
                    <i>no user</i>
                }
              </p>

              <p>
                <b>Registration Code:</b>
                &nbsp;
                {
                  device?.registrationCode ?
                    (
                      <span className="span-inline-flex-row">
                  {device?.registrationCode}
                        <button className="reload-ui-button"
                                onClick={() => navigator.clipboard.writeText(device?.registrationCode)}>
                    <FaCopy/>
                  </button>
                  </span>
                    ) :
                    (
                      <span className="span-inline-flex-row">
                  <i>none</i>
                        {
                          !device?.isRegistered &&
                          <button className="reload-ui-button" onClick={() => handleRequestRegistrationCode(deviceId)}>
                            <FaUndo/>
                          </button>
                        }
                  </span>
                    )
                }
              </p>

              <p>
                <b>Registration Timestamp:</b>
                &nbsp;
                {
                  device?.registrationCodeTimestamp &&
                  formatDate(device?.registrationCodeTimestamp, null, true)
                  || <i>none</i>
                }
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-row flex-3">
        <div className="screen-section flex-2">
          <div className="section-heading-wrapper">
            <div className="section-heading-group-row no-justify">
              <h3 className="section-heading">
                <FaGamepad/>
                Game Controls
              </h3>
              <button disabled={!activeSpecialistId || isTakeControlButtonDisabled()}
                      className={`reload-ui-button ${!(!!activeSpecialistId || isTakeControlButtonDisabled()) ? 'disabled' : ''}`}
                      onClick={() => dynamicUIRef?.current.requestUI()}><FaUndo/></button>
            </div>

            <div className="section-heading-button-wrapper">
              <button
                disabled={!activeSpecialistId || isTakeControlButtonDisabled()}
                className={`screen-header-button secondary ${!activeSpecialistId || isTakeControlButtonDisabled() ? 'disabled' : ''}`}
                onClick={() => toastDialog()}>
                <FaEnvelope/>
                Send Message
              </button>

              {
                !isTakeControlButtonDisabled() ?
                  (activeSpecialistId ?
                      <button disabled={isTakeControlButtonDisabled()}
                              className={`screen-header-button primary ${isTakeControlButtonDisabled() ? 'disabled' : ''}`}
                              onClick={() => sendSpecialistDisconnectRequest(deviceId)}>
                        <FaWandMagicSparkles/>
                        Release Control
                      </button>
                      :
                      <button disabled={isTakeControlButtonDisabled() || !isDeviceConnected}
                              className={`screen-header-button primary ${isTakeControlButtonDisabled() || !isDeviceConnected ? 'disabled' : ''}`}
                              onClick={() => sendSpecialistConnectRequest(deviceId)}>
                        <FaWandMagicSparkles/>
                        Take Control
                      </button>
                  ) :
                  <button disabled={isTakeControlButtonDisabled()}
                          className={`screen-header-button primary ${isTakeControlButtonDisabled() ? 'disabled' : ''}`}
                  >
                    <FaWandMagicSparkles/>
                    {activeSpecialistUserName}
                  </button>
              }
            </div>
          </div>

          <div className="game-controls-wrapper">
            <DynamicGameUI
              ref={dynamicUIRef}
              deviceId={deviceId}
              controlActive={!activeSpecialistId || isTakeControlButtonDisabled()}
              isDeviceConnected={isDeviceConnected}
            />
          </div>
        </div>

        <div className="screen-section flex-1">
          <div className="section-heading-wrapper">
            <div className="section-heading-group-row no-justify">
              <h3 className="section-heading">
                {/*<FaComputer/>*/}
                {/*Console*/}

                <FaInfoCircle/>
                Game State
              </h3>
            </div>
          </div>

          <div>
            {
              gameState === null ? <p>Waiting for game state output...</p> :
                Object.keys(gameState).map((gameStateKey, key) => {
                  return (
                    <p key={key}>
                      <b>{gameStateKey}:</b>
                      &nbsp;
                      {gameState[gameStateKey]}
                    </p>
                  );
                })
            }

            {/*{*/}
            {/*  consoleOutput === null ? <p>Waiting for log output...</p> :*/}
            {/*    consoleOutput.map((value, key) => {*/}
            {/*      return (*/}
            {/*        <p key={key}>*/}
            {/*          {value}*/}
            {/*        </p>*/}
            {/*      );*/}
            {/*    })*/}
            {/*}*/}
          </div>
        </div>
      </motion.div>
    </>
  )
};

const ToastMessageForm = forwardRef((props, ref) => {
  const [toastMessage, setToastMessage] = useState('');

  useImperativeHandle(ref, () => ({
    getValues,
  }));

  const getValues = () => {
    return { toastMessage };
  };

  return (
    <div className="modal-form">
      <div className="form-group">
        <label>Message</label>
        <div className="toast-message-wrapper">
          <textarea value={toastMessage} onChange={(e) => setToastMessage(e.target.value)}
                    placeholder={'Enter toast message'}/>
        </div>
      </div>
    </div>
  );
});

export default Device;
